import { Component, Injector, Input, OnInit, computed, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { BaseModalComponent } from '@card-software/library';
import { catchError, finalize, of } from 'rxjs';
import {
  AP_DOCUMENT_ENTITY_DEFINITION_CODE,
  AuthorizationCountryFormat,
  TaxAuthorization,
  TaxAuthorizationForm,
  TaxAuthorizationUpsert,
  PROVIDER_DOCUMENT_TYPES,
  PROVIDER_ENTITY_DEFINITIONS,
  SELF_ISSUED_DOCUMENT_TYPES,
  SELF_ISSUED_ENTITY_DEFINITIONS,
  AUTHORIZATION_COUNTRY_OPTIONS,
  DEFAULT_COUNTRY_CODE,
  authorizationCountryFormat,
  formatFiscalNumber,
  maxSequenceFor,
} from '../../../models/tax-authorization/tax-authorization';
import { TaxAuthorizationProxyService } from '../../../proxies/tax-authorization.proxy.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

/** Which side of the fiscal relationship the drawer is editing. */
export type TaxAuthorizationMode = 'provider' | 'self-issued';

/**
 * Add/edit tax authorization drawer. Opened through {@link ModalService} so it slides in from the right
 * like the other detail modals, and extends {@link BaseModalComponent} for the shared header/footer.
 *
 * <p>The holder is always supplied by the caller — Business Details passes the business on screen, the Tax
 * Authorizations page passes the site's own company — so there is no business picker here. That mirrors the
 * API, which binds a self-issued range's holder itself and would ignore a client-chosen one anyway.</p>
 *
 * <p>{@link mode} decides the rest: a provider (incoming) authorization is one fixed entity definition with
 * no document type, while a self-issued one picks which document it numbers.</p>
 *
 * <p><b>The band is entered in parts</b> — prefix, padding, first and last number — rather than as two
 * fully formatted strings. The formatted form is a consequence of those parts, so typing it by hand meant
 * hand-padding to the right width and keeping both ends on the same series, with a wrong fiscal number as
 * the failure mode. {@link rangePreview} shows exactly what will be issued as the fields are filled in.</p>
 *
 * <p>Owns its own create/update call so server-side validation keeps the drawer open on error; on success
 * it closes with the saved record so the parent can refresh.</p>
 */
@Component({
  selector: 'app-tax-authorization-details-modal',
  templateUrl: './tax-authorization-details-modal.component.html',
})
export class TaxAuthorizationDetailsModalComponent extends BaseModalComponent<TaxAuthorization | null> implements OnInit {
  /** Existing record when editing; null when creating. */
  @Input() authorization: TaxAuthorization | null = null;
  /** Holder of the range — required. Provider mode: the counterparty. Self-issued: the site's company. */
  @Input() businessId: string | null = null;
  /** Holder's display name, shown read-only. */
  @Input() businessName: string | null = null;
  @Input() mode: TaxAuthorizationMode = 'self-issued';

  readonly form: FormGroup<TaxAuthorizationForm>;
  saving = false;
  /** Preserved from the record so an edit never flips a system authorization to user-managed. */
  private isSystem = false;

  /** #4791 — the regime that issued the authorization; drives the prefix shape and padding. */
  readonly countryOptions = AUTHORIZATION_COUNTRY_OPTIONS;

  /** Format rules for the selected country, re-read whenever the band inputs change. */
  private readonly countryFormat = signal<AuthorizationCountryFormat | undefined>(authorizationCountryFormat(DEFAULT_COUNTRY_CODE));
  /** Bumped on every band-field change so the preview recomputes — the form is not a signal. */
  private readonly bandRevision = signal(0);

  readonly prefixHint = computed(() => this.countryFormat()?.prefixHint ?? '');

  /** Largest correlativo the regime's printed width allows — 99999999 for Honduras. Bounds both band
   *  inputs, and the API applies the same rule from its own registry. */
  readonly maxSequence = computed(() => maxSequenceFor(this.countryFormat()));

  /**
   * The first and last number this band will actually issue, or null until enough is filled in. Shown
   * because the parts are easy to get subtly wrong (a missing trailing dash, a padding of 7) in ways that
   * only become visible on a printed document.
   */
  readonly rangePreview = computed<{ start: string; end: string } | null>(() => {
    this.bandRevision();
    const { prefix, sequenceLength, rangeStart, rangeEnd } = this.form.getRawValue();
    if (!prefix || !sequenceLength || rangeStart === null || rangeEnd === null) {
      return null;
    }
    return {
      start: formatFiscalNumber(prefix, rangeStart, sequenceLength),
      end: formatFiscalNumber(prefix, rangeEnd, sequenceLength),
    };
  });

  /** How many numbers the band grants — a sanity check on a mistyped end. */
  readonly bandSize = computed(() => {
    this.bandRevision();
    const { rangeStart, rangeEnd } = this.form.getRawValue();
    if (rangeStart === null || rangeEnd === null || rangeEnd < rangeStart) {
      return null;
    }
    return rangeEnd - rangeStart + 1;
  });

  get isSelfIssued(): boolean {
    return this.mode === 'self-issued';
  }

  /** Our own company issues under any of the four; a provider's authorization only ever covers AP. */
  get entityDefinitionOptions(): { label: string; value: number }[] {
    return this.isSelfIssued ? SELF_ISSUED_ENTITY_DEFINITIONS : PROVIDER_ENTITY_DEFINITIONS;
  }

  /** The document-type field applies to AP ranges only. Every other entity definition — the dispatch
   *  documents, and the Withholding Certificate since #4801 — numbers exactly one kind of document, so the
   *  discriminator has a single possible answer and the field is hidden and cleared. */
  get isApDocumentRange(): boolean {
    return this.form.controls.entityDefinitionCode.value === AP_DOCUMENT_ENTITY_DEFINITION_CODE;
  }

  get documentTypeOptions(): { label: string; value: number }[] {
    return this.isSelfIssued ? SELF_ISSUED_DOCUMENT_TYPES : PROVIDER_DOCUMENT_TYPES;
  }

  get isEditing(): boolean {
    return this.authorization !== null;
  }

  constructor(
    private fb: FormBuilder,
    injector: Injector,
    private proxy: TaxAuthorizationProxyService,
  ) {
    super(injector);

    const defaults = authorizationCountryFormat(DEFAULT_COUNTRY_CODE);

    this.form = this.fb.group<TaxAuthorizationForm>(
      {
        entityDefinitionCode: this.fb.control<number | null>(null, Validators.required),
        businessReference: this.fb.control<string | null>(null, Validators.required),
        documentTypes: this.fb.control<number[] | null>(null),
        authorizationNumber: this.fb.control<string | null>(null, Validators.required),
        countryCode: this.fb.control<string | null>(DEFAULT_COUNTRY_CODE, Validators.required),
        prefix: this.fb.control<string | null>(null, Validators.required),
        // Read-only: the width is the country's, not the operator's, and the API resolves it again.
        sequenceLength: this.fb.control<number | null>({ value: defaults?.sequenceLength ?? null, disabled: true }),
        rangeStart: this.fb.control<number | null>(null, [Validators.required, Validators.min(0)]),
        rangeEnd: this.fb.control<number | null>(null, [Validators.required, Validators.min(0)]),
        startDate: this.fb.control<Date | null>(null, Validators.required),
        endDate: this.fb.control<Date | null>(null, Validators.required),
        active: this.fb.control<boolean | null>(true),
      },
      {
        validators: [TaxAuthorizationDetailsModalComponent.dateOrderValidator, TaxAuthorizationDetailsModalComponent.bandOrderValidator],
      },
    );

    this.applyCountryFormat(DEFAULT_COUNTRY_CODE);

    // The preview reads form values, which are not signals — nudge it on any change. Watching the whole
    // form rather than the four band controls individually keeps this to one subscription; the computed
    // only reads the band, so the extra invalidations cost nothing.
    this.form.valueChanges.subscribe(() => this.bandRevision.update((v) => v + 1));
  }

  ngOnInit(): void {
    this.form.controls.businessReference.setValue(this.businessId);

    // Both modes default to AP Document; the selector then offers all four for our own company and only
    // AP Document for a provider.
    this.form.controls.entityDefinitionCode.setValue(AP_DOCUMENT_ENTITY_DEFINITION_CODE);
    this.applyDocumentTypeValidator();

    if (!this.authorization) {
      return;
    }

    this.isSystem = this.authorization.isSystem;
    // Which document a range numbers is fixed once it exists — moving it would hand a half-consumed
    // correlativo to a different sequence, so the API pins it too.
    this.form.controls.entityDefinitionCode.setValue(this.authorization.entityDefinitionCode);
    this.form.controls.entityDefinitionCode.disable();
    this.applyDocumentTypeValidator();
    this.applyCountryFormat(this.authorization.countryCode ?? DEFAULT_COUNTRY_CODE);

    this.form.patchValue({
      businessReference: this.authorization.businessReference,
      documentTypes: this.authorization.documentTypes?.length ? [...this.authorization.documentTypes] : null,
      countryCode: this.authorization.countryCode ?? DEFAULT_COUNTRY_CODE,
      authorizationNumber: this.authorization.authorizationNumber,
      prefix: this.authorization.prefix,
      rangeStart: this.authorization.rangeStart,
      rangeEnd: this.authorization.rangeEnd,
      startDate: this.authorization.startDate ? new Date(this.authorization.startDate) : null,
      endDate: this.authorization.endDate ? new Date(this.authorization.endDate) : null,
      active: this.authorization.active,
    });
  }

  /** Picking a different self-issued document swaps whether a document type is required. */
  onEntityDefinitionChange(): void {
    this.applyDocumentTypeValidator();
  }

  /** Switching country swaps the prefix shape, the printed padding and the band ceiling. */
  onCountryChange(countryCode: string): void {
    this.applyCountryFormat(countryCode);
  }

  private applyCountryFormat(countryCode: string | null): void {
    const format = authorizationCountryFormat(countryCode);
    this.countryFormat.set(format);

    const prefix = this.form.controls.prefix;
    prefix.setValidators(format ? [Validators.required, Validators.pattern(format.prefixPattern)] : [Validators.required]);
    prefix.updateValueAndValidity({ emitEvent: false });
    this.bandRevision.update((v) => v + 1);
  }

  /** An AP range must cover at least one document — that list is what a document is matched against, so
   *  an empty one would be unreachable in both directions. The dispatch ranges carry none. */
  private applyDocumentTypeValidator(): void {
    const documentTypes = this.form.controls.documentTypes;
    if (this.isApDocumentRange) {
      documentTypes.setValidators([Validators.required]);
    } else {
      documentTypes.clearValidators();
      documentTypes.setValue(null);
    }
    documentTypes.updateValueAndValidity();
  }

  /** End date must fall strictly after the start date (not on or before it). */
  private static dateOrderValidator(group: AbstractControl): ValidationErrors | null {
    const start = group.get('startDate')?.value as Date | null;
    const end = group.get('endDate')?.value as Date | null;
    if (start && end && end.getTime() <= start.getTime()) {
      return { dateOrder: true };
    }
    return null;
  }

  /** An inverted band would be rejected by the API; catch it before the round trip. */
  private static bandOrderValidator(group: AbstractControl): ValidationErrors | null {
    const start = group.get('rangeStart')?.value as number | null;
    const end = group.get('rangeEnd')?.value as number | null;
    if (start !== null && end !== null && end < start) {
      return { bandOrder: true };
    }
    return null;
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      GlobalMessageUtil.warn('Tax Authorizations', 'Complete all required fields before saving.');
      return;
    }

    const value = this.form.getRawValue();
    const payload: TaxAuthorizationUpsert = {
      businessReference: value.businessReference!,
      documentTypes: value.documentTypes ?? [],
      authorizationNumber: value.authorizationNumber!.trim(),
      countryCode: value.countryCode!,
      prefix: value.prefix!.trim(),
      rangeStart: value.rangeStart!,
      rangeEnd: value.rangeEnd!,
      startDate: value.startDate!.toISOString(),
      endDate: value.endDate!.toISOString(),
      active: value.active ?? true,
      entityDefinitionCode: value.entityDefinitionCode!,
      isSystem: this.isSystem,
    };

    const id = this.authorization?.id;
    if (id) {
      payload.id = id;
    }

    this.saving = true;
    const op$ = id ? this.proxy.update(id, payload) : this.proxy.create(payload);

    op$
      .pipe(
        catchError(() => of(null)),
        finalize(() => (this.saving = false)),
      )
      .subscribe((response) => {
        if (response === null || !response.success) {
          return;
        }
        GlobalMessageUtil.success('Tax Authorizations', id ? 'Tax authorization updated.' : 'Tax authorization created.');
        this.close({ action: 'Save', data: response.data ?? null });
      });
  }

  onClose(): void {
    this.close({ action: 'Cancel', data: null });
  }
}
